/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import clsx from 'clsx';

import { isValidHref } from '@tock/shared/utils/hrefValidate';

import BaseComponent from '@tock/shared/components/BaseComponent';

export interface AnchorProps
  extends React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  > {
  'data-testid'?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: React.Ref<any>;
  reload?: boolean;
}

interface State {
  href?: string;
  isChangingContext: boolean;
}

const httpPattern = /.*:.*/;

/**
 * This component acts as a wrapper around the react-router-dom <Link/> component or alternately will
 * render as a plain <a/> in the case of an externally-directed href, or a link that is just
 * to a different #hash location on the same page.
 */
class Anchor extends BaseComponent<AnchorProps, State> {
  /**
   * Render a standard HTML anchor.
   * Used for all outbound links and internal anchor links (ex: #go-to-this-id)
   */
  renderAnchor() {
    const { children, className, target } = this.props;
    const linkProps = _.omit(
      this.props,
      'children',
      'className',
      'reload',
      'role' // we omit any explicit role prop because <a> implicitly has a link role and that is what we want
    );

    return (
      <a
        className={clsx(className, 'Anchor')}
        rel={target === '_blank' ? 'noopener' : undefined}
        // this includes linkProps.onClick
        {...linkProps}
      >
        {children}
      </a>
    );
  }

  render() {
    const { children, reload, ...rest } = this.props;

    // prevent XSS attacks
    rest.href = isValidHref(rest.href) ? rest.href : '';

    const isOutbound = httpPattern.test(rest.href || '');
    const isAnchorLink = rest.href && rest.href.startsWith('#');

    if (rest.href && !isOutbound && !reload && !isAnchorLink) {
      const props = {
        ...rest,
        className: clsx(rest.className, 'Anchor'),
        to: rest.href,
      };
      delete props.href;
      return (
        // Note that props includes an optional props.onClick, this is handled
        // correctly just with the spread here.
        <Link {...props}>{children}</Link>
      );
    } else {
      return this.renderAnchor();
    }
  }
}

export default Anchor;
